import React, { useEffect, useRef, useState } from 'react';
import {
  Grid,
  Typography,
  Paper,
  Button,
  Divider,
  Container,
  Avatar,
  useTheme,
  Box,
  CircularProgress,
  Snackbar,
  Alert,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Fade,
  Slide,
} from '@mui/material';
import CreditCardIcon from '@mui/icons-material/CreditCard';
import QrCodeIcon from '@mui/icons-material/QrCode';
import CloseIcon from '@mui/icons-material/Close';
import PhoneIcon from '@mui/icons-material/Phone';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { useLocation, useNavigate } from 'react-router-dom';
import AuthService from '../Auth/AuthService';
import QRCode from "react-qr-code";

const Transition = React.forwardRef(function Transition(props, ref) {
  return <Slide direction="up" ref={ref} {...props} />;
});

const CheckoutPage = () => {
  const theme = useTheme();
  const location = useLocation();
  const plan = location.state?.plan;
  const coupon = location.state?.coupon;
  // const newPlan = { ...plan, couponDTO: { ...coupon } };
  // const navigate = useNavigate();
  const hasFetched = useRef(false);
  const [billingInfo, setBillingInfo] = useState();
  const [payableAmount, SetPayableAmount] = useState({});
  const [loading, setLoading] = useState(false);
  const [upiLink, setUpiLink] = useState(null);
  const [showQRDialog, setShowQRDialog] = useState(false);
  const [showPostPaymentDialog, setShowPostPaymentDialog] = useState(false);
  // const [paymentStatus, setPaymentStatus] = useState('pending');
  const [orderId, setOrderId] = useState(null);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'info',
  });

  // Configuration
  const UPI_CONFIG = {
    UPI_ID: '8055942603@upi', // Replace with your actual UPI ID
    MERCHANT_NAME: 'JMK',
    MERCHANT_CODE: 'YOUR_MERCHANT_CODE',
    TRANSACTION_NOTE: 'Subscription Payment',
  };

  // Support contact details
  const SUPPORT_CONFIG = {
    PHONE_NUMBER: '+91 8055942603', // Replace with your actual number
    WHATSAPP_NUMBER: '+91 8055942603',
    SUPPORT_EMAIL: 'jmk.infotech2021@gmail.com.com',
    SUPPORT_HOURS: '9:00 AM - 6:00 PM (IST)',
  };

  useEffect(() => {
    if (hasFetched.current) return;
    hasFetched.current = true;
    loadData();
  }, []);

  const getDiscountedPrice = (plan) => {
    if (!plan.couponDTO) return null;
    const { discountType, discountPercentage, discountAmount } = plan.couponDTO;
    if (discountType === 'PERCENTAGE' && discountPercentage) {
      return plan.price * (1 - discountPercentage / 100);
    }
    if (discountType === 'FLAT' && discountAmount) {
      return Math.max(0, plan.price - discountAmount);
    }
    return null;
  };

  const loadData = async () => {
    try {
      const url = AuthService.pathUrl("GET", "/auth");
      const response = await AuthService.getAuth(url);
      setBillingInfo(response || []);
      
      let finalPrice = plan.price;
      if (plan.couponDTO) {
        const discountedPrice = getDiscountedPrice(plan);
        if (discountedPrice !== null) {
          finalPrice = discountedPrice;
        }
      }

      const payable = {
        amount: Math.round(finalPrice + (finalPrice * 15 / 100)),
        subtotal: finalPrice,
        tax: Math.round(finalPrice * 15 / 100),
        currency: 'INR',
        loginId: response?.id,
        planId: plan?.id,
        couponId: plan?.coupon?.id ? plan?.coupon?.id : coupon?.id,
        merchantId: UPI_CONFIG.MERCHANT_CODE,
      };
      SetPayableAmount(payable);
    } catch (error) {
      console.error("Failed to fetch user:", error.message);
      setSnackbar({
        open: true,
        message: 'Failed to load user details',
        severity: 'error',
      });
    }
  };

  const createOrder = async () => {
    try {
      const url = AuthService.pathUrl('POST', '/payment/order');
      const response = await AuthService.postAuth(url, payableAmount);
      return response;
    } catch (error) {
      console.error("Failed to create order:", error.message);
      throw error;
    }
  };

  // Generate UPI payment URL
  const generateUPIUrl = (amount, transactionId = null) => {
    const baseUrl = 'upi://pay';
    const params = new URLSearchParams({
      pa: UPI_CONFIG.UPI_ID,
      pn: UPI_CONFIG.MERCHANT_NAME,
      am: amount.toFixed(2),
      cu: 'INR',
      tn: `${UPI_CONFIG.TRANSACTION_NOTE} - ${transactionId || 'TXN'}`,
      mc: UPI_CONFIG.MERCHANT_CODE,
    });

    return `${baseUrl}?${params.toString()}`;
  };

  const handlePayment = async () => {
    setLoading(true);
    try {
      const order = await createOrder();
      
      if (!order || !order.id) {
        throw new Error('Failed to create payment order');
      }

      setOrderId(order.id);
      const upiUrl = generateUPIUrl(payableAmount.amount, order.id);
      setUpiLink(upiUrl);
      setShowQRDialog(true);

      const isMobile = /Android|iPhone|iPad|iPod/i.test(navigator.userAgent);

      if (isMobile) {
        const upiDeepLink = `upi://pay?pa=${UPI_CONFIG.UPI_ID}&pn=${encodeURIComponent(UPI_CONFIG.MERCHANT_NAME)}&am=${payableAmount.amount}&cu=INR&tn=${encodeURIComponent(`${UPI_CONFIG.TRANSACTION_NOTE} - ${order.id}`)}`;
        
        // Try to open UPI app
        window.location.href = upiDeepLink;
        
        // Show QR dialog as fallback if UPI app doesn't open
        setTimeout(() => {
          setShowQRDialog(true);
        }, 1000);
      }

      setSnackbar({
        open: true,
        message: 'Payment initiated. Please complete the payment in your UPI app.',
        severity: 'success',
      });

    } catch (error) {
      console.error("Payment failed:", error.message);
      setSnackbar({
        open: true,
        message: `Payment failed: ${error.message}`,
        severity: 'error',
      });
    } finally {
      setLoading(false);
    }
  };

  // Handle manual payment confirmation
  const handleManualPaymentConfirmation = () => {
    setShowQRDialog(false);
    // setPaymentStatus('success');
    setShowPostPaymentDialog(true);
    
    setSnackbar({
      open: true,
      message: 'Payment confirmed! Please call support to activate your subscription.',
      severity: 'success',
    });
  };

  const copyUPIId = () => {
    navigator.clipboard.writeText(UPI_CONFIG.UPI_ID);
    setSnackbar({
      open: true,
      message: 'UPI ID copied to clipboard',
      severity: 'success',
    });
  };

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  const handleCloseQRDialog = () => {
    setShowQRDialog(false);
  };

  const handleClosePostPaymentDialog = () => {
    setShowPostPaymentDialog(false);
    // Navigate to dashboard or subscription page
    // navigate('/dashboard');
  };

  // Call support number
  const callSupport = () => {
    window.location.href = `tel:${SUPPORT_CONFIG.PHONE_NUMBER.replace(/\s/g, '')}`;
  };

  // Open WhatsApp
  const openWhatsApp = () => {
    const message = encodeURIComponent('Hi, I need help with my subscription payment. Order ID: ' + (orderId || 'N/A'));
    window.open(`https://wa.me/${SUPPORT_CONFIG.WHATSAPP_NUMBER.replace(/\s/g, '')}?text=${message}`, '_blank');
  };

  if (!plan) {
    return (
      <Container maxWidth="sm" sx={{ py: 10 }}>
        <Typography variant="h6" align="center">
          No subscription plan selected.
        </Typography>
      </Container>
    );
  }

  return (
    <Container maxWidth="md" sx={{ py: 6, justifyItems: 'center' }}>
      <Typography variant="h4" align="center" fontWeight="bold" gutterBottom>
        Complete Your Purchase
      </Typography>

      <Grid container spacing={4} mt={2}>
        <Grid item xs={12} md={5}>
          <Paper
            elevation={5}
            sx={{
              p: 4,
              width: '100%',
              background: theme.palette.mode === 'light' ? '#f5faff' : '#1e1e1e',
              border: '1px solid',
              borderColor: theme.palette.primary.main,
            }}
          >
            <Avatar sx={{ bgcolor: '#1b1914', mb: 2 }}>
              <CreditCardIcon />
            </Avatar>
            <Typography variant="h6">{plan.name}</Typography>
            <Typography variant="body2" color="text.secondary">
              Tax: 15% included
            </Typography>
            
            {plan.couponDTO && (
              <Box sx={{ mt: 1, p: 1, bgcolor: 'success.light', borderRadius: 1 }}>
                <Typography variant="body2" color="success.dark">
                  🎉 Coupon Applied!
                </Typography>
                <Typography variant="body2" color="success.dark">
                  Discount: {plan.couponDTO.discountType === 'PERCENTAGE' 
                    ? `${plan.couponDTO.discountPercentage}%` 
                    : `₹${plan.couponDTO.discountAmount}`}
                </Typography>
                <Typography variant="body2" color="success.dark">
                  Subtotal: ₹{payableAmount?.subtotal || plan.price}
                </Typography>
              </Box>
            )}

            <Typography variant="h6" color="primary" fontWeight="bold" my={1}>
              Subtotal: ₹{payableAmount?.subtotal || plan.price}
            </Typography>
            <Typography variant="h6" color="primary" fontWeight="bold" my={1}>
              Tax (15%): ₹{payableAmount?.tax}
            </Typography>
            <Typography variant="h5" color="primary.main" fontWeight="bold" my={1}>
              Total: ₹{payableAmount?.amount}
            </Typography>
            <Typography variant="body1" color="text.secondary">
              Duration: {plan.durationInDays} Days
            </Typography>
            
            <Divider sx={{ my: 2 }} />
            
            <Typography fontWeight="bold" gutterBottom>
              Features:
            </Typography>
            <Box my={2} sx={{ height: 'auto' }}>
              {plan?.features?.map((f, i) => (
                <Typography key={i} variant="body2" color="primary">
                  ✅ {f}
                </Typography>
              ))}
            </Box>

            <Divider sx={{ mt: 3 }} />
            <Typography variant="h6" gutterBottom>
              Billing Details
            </Typography>
            <Divider sx={{ mb: 3 }} />

            <Grid container spacing={2}>
              <Grid item xs={12}>
                <Typography variant="body2">
                  <strong>Name:</strong> {billingInfo?.name || billingInfo?.masterCode || 'N/A'}
                </Typography>
                <Typography variant="body2">
                  <strong>Email:</strong> {billingInfo?.email || 'N/A'}
                </Typography>
                <Typography variant="body2">
                  <strong>Mobile:</strong> {billingInfo?.mobile || 'N/A'}
                </Typography>
              </Grid>
            </Grid>

            <Button
              fullWidth
              variant="contained"
              sx={{
                mt: 4,
                py: 1.5,
                fontSize: '1rem',
                fontWeight: 'bold',
                borderRadius: '40px',
                backgroundColor: '#1b1914',
                '&:hover': { backgroundColor: '#503e02ff' },
                '&:disabled': { opacity: 0.6 },
              }}
              onClick={handlePayment}
              disabled={loading}
            >
              {loading ? (
                <CircularProgress size={24} color="inherit" />
              ) : (
                `Pay ₹${payableAmount?.amount} via UPI`
              )}
            </Button>

            <Button
              fullWidth
              variant="outlined"
              sx={{
                mt: 2,
                py: 1,
                fontSize: '0.9rem',
                borderRadius: '40px',
              }}
              onClick={copyUPIId}
            >
              Copy UPI ID for Manual Payment
            </Button>

            <Box sx={{ mt: 2, textAlign: 'center' }}>
              <Typography variant="caption" color="text.secondary">
                Need help? Contact support: {SUPPORT_CONFIG.PHONE_NUMBER}
              </Typography>
            </Box>
          </Paper>
        </Grid>
      </Grid>

      {/* QR Code Dialog */}
      <Dialog
        open={showQRDialog}
        onClose={handleCloseQRDialog}
        maxWidth="sm"
        fullWidth
        TransitionComponent={Transition}
      >
        <DialogTitle>
          Complete Payment via UPI
          <IconButton
            aria-label="close"
            onClick={handleCloseQRDialog}
            sx={{ position: 'absolute', right: 8, top: 8 }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <Box sx={{ textAlign: 'center', py: 2 }}>
            <Typography variant="h6" gutterBottom>
              Amount: ₹{payableAmount?.amount}
            </Typography>
            
            {/* QR Code Display - Now with proper QR code generation */}
            <Box
              sx={{
                width: 250,
                height: 250,
                margin: '20px auto',
                border: '2px solid #e0e0e0',
                borderRadius: 2,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                backgroundColor: 'white',
                p: 2,
              }}
            >
              {upiLink ? (
                <QRCode
                  value={upiLink}
                  size={200}
                  bgColor="#ffffff"
                  fgColor="#000000"
                  level="H"
                  includeMargin={true}
                  renderAs="canvas" // or 'svg'
                />
              ) : (
                <QrCodeIcon sx={{ fontSize: 80, color: '#999' }} />
              )}
            </Box>

            <Typography variant="body2" color="text.secondary" gutterBottom>
              Scan this QR code with any UPI app (Google Pay, PhonePe, Paytm, etc.)
            </Typography>

            <Divider sx={{ my: 2 }} />

            <Typography variant="body2" gutterBottom>
              <strong>UPI ID:</strong> {UPI_CONFIG.UPI_ID}
            </Typography>
            
            <Typography variant="body2" gutterBottom>
              <strong>Order ID:</strong> {orderId || 'N/A'}
            </Typography>

            <Box sx={{ mt: 2, display: 'flex', gap: 2, justifyContent: 'center', flexWrap: 'wrap' }}>
              <Button
                variant="contained"
                size="small"
                onClick={copyUPIId}
                sx={{ borderRadius: 20 }}
              >
                Copy UPI ID
              </Button>
              <Button
                variant="contained"
                color="success"
                size="small"
                onClick={handleManualPaymentConfirmation}
                sx={{ borderRadius: 20 }}
              >
                I've Completed Payment
              </Button>
              {navigator.userAgent.match(/Android|iPhone|iPad|iPod/i) && (
                <Button
                  variant="contained"
                  color="primary"
                  size="small"
                  onClick={() => {
                    if (upiLink) {
                      window.location.href = upiLink;
                    }
                  }}
                  sx={{ borderRadius: 20 }}
                >
                  Open UPI App
                </Button>
              )}
            </Box>

            <Typography variant="caption" color="text.secondary" sx={{ mt: 2, display: 'block' }}>
              After payment, click "I've Completed Payment" to confirm
            </Typography>
          </Box>
        </DialogContent>
      </Dialog>

      {/* Post-Payment Dialog */}
      <Dialog
        open={showPostPaymentDialog}
        onClose={handleClosePostPaymentDialog}
        maxWidth="sm"
        fullWidth
        TransitionComponent={Transition}
      >
        <DialogTitle sx={{ textAlign: 'center' }}>
          <Box sx={{ display: 'flex', justifyContent: 'center', mb: 2 }}>
            <CheckCircleIcon sx={{ fontSize: 64, color: 'success.main' }} />
          </Box>
          Payment Successful! 🎉
        </DialogTitle>
        <DialogContent>
          <Fade in={showPostPaymentDialog} timeout={500}>
            <Box sx={{ textAlign: 'center', py: 2 }}>
              <Typography variant="h6" gutterBottom color="success.main">
                Your subscription is now active!
              </Typography>
              
              <Divider sx={{ my: 3 }} />
              
              <Box 
                sx={{ 
                  p: 3, 
                  // bgcolor: 'warning.light', 
                  borderRadius: 2,
                  mb: 3,
                  border: '2px solid #ff9800',
                }}
              >
                <Typography variant="h6" color="warning.dark" gutterBottom>
                  ⚠️ Important: To activate the service, share the payment screenshot on WhatsApp using your registered mobile number.
                </Typography>
                <Typography variant="body1" sx={{ mt: 2 }}>
                  Please call our support team to complete the activation process.
                </Typography>
              </Box>

              <Box 
                sx={{ 
                  p: 3, 
                  // bgcolor: 'primary.light', 
                  borderRadius: 2,
                  mb: 3,
                }}
              >
                <Typography variant="body2" gutterBottom>
                  <strong>📞 Call us at:</strong>
                </Typography>
                <Typography variant="h6" sx={{ my: 2, fontWeight: 'bold', color: 'primary.main' }}>
                  {SUPPORT_CONFIG.PHONE_NUMBER}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Available: {SUPPORT_CONFIG.SUPPORT_HOURS}
                </Typography>
              </Box>

              <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center', flexWrap: 'wrap' }}>
                <Button
                  variant="contained"
                  color="primary"
                  startIcon={<PhoneIcon />}
                  onClick={callSupport}
                  sx={{ 
                    borderRadius: 30,
                    py: 0.5,
                    px: 4,
                    fontSize: '1rem',
                  }}
                >
                  Call Now
                </Button>
                <Button
                  variant="contained"
                  color="success"
                  onClick={openWhatsApp}
                  sx={{ 
                    borderRadius: 30,
                    py: 0.5,
                    px: 2,
                  }}
                >
                  WhatsApp
                </Button>
                <Button
                  variant="outlined"
                  onClick={handleClosePostPaymentDialog}
                  sx={{ borderRadius: 30 }}
                >
                  Close
                </Button>
              </Box>

              <Box sx={{ mt: 3, p: 2, bgcolor: 'grey.100', borderRadius: 1 }}>
                <Typography variant="body2" color="text.secondary">
                  <strong>Order ID:</strong> {orderId || 'N/A'}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  <strong>Plan:</strong> {plan?.name}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  <strong>Amount Paid:</strong> ₹{payableAmount?.amount}
                </Typography>
              </Box>

              <Typography variant="caption" color="text.secondary" sx={{ mt: 2, display: 'block' }}>
                After calling, your subscription will be activated within 24 hours.
              </Typography>
            </Box>
          </Fade>
        </DialogContent>
      </Dialog>

      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert onClose={handleCloseSnackbar} severity={snackbar.severity} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Container>
  );
};

export default CheckoutPage;